import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import { type IdentityId, type User } from '~types'

type UserPermissionObject = {
  timestamp: number
  permissions: Record<string, IdentityId[]>
}

type UserAdHocPermissionObject = {
  timestamp: number
  permissions: Record<string, string[]>
  resourcePermissions: Record<string, string[]>
}

interface AuthState {
  userId: string | null
  user: User | null
  isAuthenticated: boolean
  isOutlookMode: boolean
  sessionId: string | null
  sessionToken: string | null
  userPermissions: UserPermissionObject | null
  userAdHocPermissions: UserAdHocPermissionObject | null
  sessionExpiresAt: string | null
}

const initialState: AuthState = {
  userId: null,
  user: null,
  isAuthenticated: false,
  isOutlookMode: false,
  sessionId: null,
  sessionToken: null,
  userPermissions: null,
  userAdHocPermissions: null,
  sessionExpiresAt: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<User>) {
      const userObj = action.payload
      state.userId = userObj.id
      state.user = userObj
      state.sessionExpiresAt = userObj.session_expires_at ?? null
    },
    setUserId(state, action: PayloadAction<string>) {
      state.userId = action.payload
    },
    setAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload
    },
    setUnauthenticated(state) {
      state.isAuthenticated = false
      state.user = null
      state.userPermissions = null
      state.userAdHocPermissions = null
      state.sessionExpiresAt = null
    },
    updateUserInfo(state, action: PayloadAction<User>) {
      state.user = action.payload
      state.sessionExpiresAt = action.payload.session_expires_at ?? null
    },
    loginUser(state, action: PayloadAction<User>) {
      state.userId = action.payload.id
      state.user = action.payload
      state.isAuthenticated = true
      state.userPermissions = null
      state.userAdHocPermissions = null
      state.sessionExpiresAt = action.payload.session_expires_at ?? null
    },
    logoutUser(state) {
      state.userId = null
      state.user = null
      state.isAuthenticated = false
      state.isOutlookMode = false
      state.sessionId = null
      state.sessionToken = null
      state.userPermissions = null
      state.userAdHocPermissions = null
      state.sessionExpiresAt = null
    },
    setSessionId(state, action: PayloadAction<string>) {
      state.sessionId = action.payload
    },
    setSessionToken(state, action: PayloadAction<string>) {
      state.sessionToken = action.payload
    },
    setIsOutlookMode(state, action: PayloadAction<boolean>) {
      state.isOutlookMode = action.payload
    },
    setUserPermissions(state, action: PayloadAction<UserPermissionObject>) {
      state.userPermissions = action.payload
    },
    setUserAdHocPermissions(state, action: PayloadAction<UserAdHocPermissionObject>) {
      state.userAdHocPermissions = action.payload
    },
  },
})

export const {
  setUser,
  setUserId,
  setAuthenticated,
  setUnauthenticated,
  updateUserInfo,
  loginUser,
  logoutUser,
  setIsOutlookMode,
  setSessionId,
  setSessionToken,
  setUserPermissions,
  setUserAdHocPermissions,
} = authSlice.actions

export default authSlice.reducer
